import { apiClient, getApiBaseURL } from './client';

/**
 * List certificates / completion status for a course (admin/teacher view)
 * @param {string} courseId
 * @param {{ page?, limit? }} params
 */
export async function getCourseCertificates(courseId, params = {}) {
  const { data } = await apiClient.get(`/certificates/courses/${courseId}`, { params });
  return data;
}

/**
 * Get the certificate for a specific enrollment (student-only view)
 * @param {string} enrollmentId
 */
export async function getCertificate(enrollmentId) {
  const { data } = await apiClient.get(`/certificates/${enrollmentId}`);
  return data;
}

/**
 * Admin/teacher: generate a certificate for a completed enrollment
 * @param {string} courseId
 * @param {string} enrollmentId
 */
export async function generateCourseCertificate(courseId, enrollmentId) {
  const { data } = await apiClient.post(
    `/certificates/courses/${courseId}/enrollments/${enrollmentId}/generate`,
  );
  return data;
}

/**
 * Get public certificate preview
 * @param {string} shareToken
 */
export async function getPublicCertificate(shareToken) {
  const { data } = await apiClient.get(`/certificates/public/${shareToken}`);
  return data;
}

/**
 * Generate certificate share link
 * @param {string} certificateId
 */
export async function getCertificateShareLink(certificateId) {
  const { data } = await apiClient.post(`/certificates/${certificateId}/share`);
  return data;
}

// ── Certificate designs + course settings (shared with the admin panel) ─────────

/** Catalogue of certificate designs (public route). */
export async function getCertificateTemplates() {
  const { data } = await apiClient.get('/public/certificate-templates');
  return Array.isArray(data) ? data : [];
}

/** Course's design, English title, authors and issued/revoked counts (TEACHER/MENTOR/ADMIN). */
export async function getCourseCertificateSettings(courseId) {
  const { data } = await apiClient.get(`/certificates/courses/${courseId}/settings`);
  return data;
}

/**
 * Update any of { templateId, certificateTitleEn, authors: [{ name, title, titleEn }] (0..3) }.
 * Applies to certificates issued from now on; issued ones keep their design and authors.
 */
export async function updateCourseCertificateSettings(courseId, payload) {
  const { data } = await apiClient.put(`/certificates/courses/${courseId}/settings`, payload);
  return data;
}

/** SAMPLE PDF (watermarked) of the course's certificate in a design. */
export async function getCourseCertificateSample(courseId, templateId) {
  const { data } = await apiClient.get(`/certificates/courses/${courseId}/sample`, {
    params: { templateId },
    responseType: 'blob',
  });
  return data;
}

/** ADMIN: revoke with a reason (3..300 chars). */
export async function revokeCertificate(certificateId, reason) {
  const { data } = await apiClient.post(`/admin/certificates/${certificateId}/revoke`, { reason });
  return data;
}

/** ADMIN: re-issue in place (new number + verify code), optionally with a corrected name. */
export async function reissueCertificate(certificateId, studentName) {
  const { data } = await apiClient.post(
    `/admin/certificates/${certificateId}/reissue`,
    studentName ? { studentName } : {},
  );
  return data;
}

/** Absolute URL of an API-relative asset path such as `/api/v1/public/...`. */
export function certificateAssetUrl(path) {
  if (!path) return null;
  const base = getApiBaseURL().replace(/\/api\/v\d+\/?$/, '');
  return `${base}${path}`;
}

/** Public PDF of an issued or revoked certificate (staff can open it without the owner's token). */
export function publicCertificatePdfUrl(verifyCode) {
  if (!verifyCode) return null;
  return `${getApiBaseURL()}/public/certificates/${verifyCode}/pdf`;
}
